import styles from './styles.module.css';
import { Box, Grid, Tooltip, TextField, MenuItem, Button } from '@mui/material';
import DataTable from 'components/utils/DataTable';
import Info from 'components/utils/Alert/Info';
import { AlertMessagesContext } from 'react-alert-messages';
import React, { useState, useContext, useEffect } from 'react';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import FailedJobService from 'services/FailedJobService';
import { useTranslation } from 'react-i18next';

const JOB_TYPES = [{ name: 'Sale', value: 'sale' }];

function FailedJobs() {
  const { t: tCommon } = useTranslation('common');
  const { t: tManagement } = useTranslation('management');
  const { postAlertMessage } = useContext(AlertMessagesContext);

  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [failedJobs, setFailedJobs] = useState([]);
  const [jobType, setJobType] = useState('sale');

  const headerData = [
    {
      label: tCommon('Type'),
      id: 'type',
      type: 'text',
    },
    {
      label: tCommon('Error'),
      id: 'error',
      type: 'text',
    },
    {
      label: tCommon('Actions'),
      id: 'download',
      cssClass: 'no-print',
      cssClassHeader: 'no-print',
      type: 'callback',
      viewRender: (data) => {
        return (
          <Grid className={styles.btnGroup}>
            <Tooltip title={tCommon('Re sync')} onClick={() => handleResyncJobsBatch([data])}>
              <RestartAltIcon className={styles.iconBtn} />
            </Tooltip>
          </Grid>
        );
      },
    },
  ];

  const handleResyncJobsBatch = async () => {
    for (let i = 0; i < failedJobs.length; i += 2) {
      const batch = failedJobs.slice(i, i + 2);
      await handleReSyncJobs(batch);
    }
    await getFailedJobs();
  };

  const handleReSyncJobs = async (data) => {
    setLoadingIndicator(true);
    try {
      await FailedJobService.syncFiledJobs(data);
    } catch (error) {
      postAlertMessage({ text: error?.message, type: 'failed' });
    } finally {
      setLoadingIndicator(false);
    }
  };

  const getFailedJobs = async ({ _filterJobType = jobType } = {}) => {
    setLoadingIndicator(true);
    try {
      const params = { type: _filterJobType, limit: 30 };
      const res = await FailedJobService.getFailedJobs(params);
      setFailedJobs(res);
    } catch (error) {
      postAlertMessage({ text: error?.message, type: 'failed' });
    }
    setLoadingIndicator(false);
  };

  useEffect(() => {
    getFailedJobs();
    // eslint-disable-next-line
  }, []);

  return (
    <div className={styles.contentLayout}>
      <div className={styles.settingsGrid}>
        <Box container spacing={2}>
          {failedJobs && failedJobs.length ? (
            <>
              <Box className={styles.subHeader}>
                <span>{tCommon('Job Actions')}</span>
              </Box>
              <Box p={2}>
                <Grid container>
                  <Grid item xs={6}>
                    <label className={styles.machineSelectTitle}>{tCommon('Job type')}</label>
                    <TextField
                      style={{ marginTop: '15px', marginBottom: '15px' }}
                      select
                      name="selectedMachine"
                      size="small"
                      variant="outlined"
                      color="primary"
                      fullWidth
                      value={jobType}
                      onChange={(event) => {
                        setJobType(event.target.value);
                        getFailedJobs({ _filterJobType: event.target.value });
                      }}
                    >
                      {(JOB_TYPES || []).map((type) => (
                        <MenuItem key={type.name} value={type.value}>
                          {type.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={6} display="flex" justifyContent="flex-end">
                    <Button variant="contained" onClick={handleResyncJobsBatch} style={{ height: '50px' }}>
                      {tCommon('Re-sync Jobs')}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
              <Box className={styles.subHeader}>
                <span>{tCommon('Failed Jobs')}</span>
              </Box>
              <DataTable columns={headerData} rows={failedJobs || []} isLoading={loadingIndicator} />
            </>
          ) : (loadingIndicator || failedJobs.length) ? (
            <DataTable columns={headerData} rows={failedJobs || []} isLoading={loadingIndicator} />
          ) : (
            !loadingIndicator && (
              <Info
                severity="success"
                title={tManagement('All your jobs are synced')}
                content={tManagement('You have no failed jobs to sync.')}
              />
            )
          )}
        </Box>
      </div>
    </div>
  );
}

export default FailedJobs;
